import { ENGLISH_LOCALE, SPANISH_LOCALE, isEnglishLocale, isSpanishLocale } from './locale';

const HOME_SLUGS = {
  es: 'home',
  en: 'homeeng',
};

const SPANISH_HOME_ALIASES = new Set(['', 'home', 'inicio', 'index']);
const ENGLISH_HOME_ALIASES = new Set(['homeeng']);

export const normalizeRouteSlug = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/^\/+|\/+$/g, '')
  .replace(/\?.*$/, '');

export const getHomeSlugForLocale = (locale) => (
  isEnglishLocale(locale) ? HOME_SLUGS.en : HOME_SLUGS.es
);

export const getLocaleForLocalizedSlug = (slug) => {
  const normalizedSlug = normalizeRouteSlug(slug);

  if (ENGLISH_HOME_ALIASES.has(normalizedSlug)) return ENGLISH_LOCALE;
  if (SPANISH_HOME_ALIASES.has(normalizedSlug)) return SPANISH_LOCALE;

  return null;
};

export const isHomeSlug = (slug) => {
  const normalizedSlug = normalizeRouteSlug(slug);
  return SPANISH_HOME_ALIASES.has(normalizedSlug) || ENGLISH_HOME_ALIASES.has(normalizedSlug);
};

export const resolveSlugForLocale = (slug, locale) => {
  const normalizedSlug = normalizeRouteSlug(slug);

  if (isHomeSlug(normalizedSlug)) {
    return getHomeSlugForLocale(locale);
  }

  return normalizedSlug;
};

export const resolveUrlForLocale = (url = '', locale) => {
  const rawUrl = String(url || '').trim();
  if (!rawUrl) return rawUrl;

  if (
    rawUrl.startsWith('#')
    || rawUrl.startsWith('//')
    || /^[a-z][a-z0-9+.-]*:/i.test(rawUrl)
  ) {
    return rawUrl;
  }

  if (rawUrl.startsWith('?') || rawUrl.startsWith('/?')) {
    return rawUrl.startsWith('/?') ? rawUrl : `/${rawUrl}`;
  }

  const [pathWithQuery, hash = ''] = rawUrl.split('#');
  const [pathOnly, query = ''] = pathWithQuery.split('?');
  const normalizedPath = normalizeRouteSlug(pathOnly);
  const hasLeadingSlash = pathOnly.startsWith('/');

  if (isHomeSlug(normalizedPath)) {
    if (query) {
      return `/?${query}${hash ? `#${hash}` : ''}`;
    }

    const resolvedPath = `/${getHomeSlugForLocale(locale)}`;
    return `${resolvedPath}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
  }

  return hasLeadingSlash ? rawUrl : `/${rawUrl}`;
};

export const localeMatchesLocalizedSlug = (slug, locale) => {
  const slugLocale = getLocaleForLocalizedSlug(slug);
  if (!slugLocale) return true;

  return slugLocale === ENGLISH_LOCALE
    ? isEnglishLocale(locale)
    : isSpanishLocale(locale);
};

const getLocalizationEntries = (page) => {
  const localizations = page?.localizations?.data || page?.localizations || [];
  return Array.isArray(localizations) ? localizations : [];
};

const getLocalizationValue = (entry, field) => entry?.[field] ?? entry?.attributes?.[field];

export const getLocalizedPageSlug = (page, targetLocale) => {
  if (!page) return '';

  const pageLocale = page.locale || page.attributes?.locale;
  const pageSlug = page.slug || page.attributes?.slug;

  if (
    (isEnglishLocale(targetLocale) && isEnglishLocale(pageLocale))
    || (isSpanishLocale(targetLocale) && isSpanishLocale(pageLocale))
  ) {
    return normalizeRouteSlug(pageSlug);
  }

  const match = getLocalizationEntries(page).find((entry) => {
    const entryLocale = getLocalizationValue(entry, 'locale');
    return isEnglishLocale(targetLocale)
      ? isEnglishLocale(entryLocale)
      : isSpanishLocale(entryLocale);
  });

  return normalizeRouteSlug(getLocalizationValue(match, 'slug'));
};

export const getLocalizedPagePath = (page, targetLocale) => {
  const localizedSlug = getLocalizedPageSlug(page, targetLocale);
  if (!localizedSlug) return '';

  return `/${localizedSlug}`;
};

const normalizeHomeDeeplinkSlug = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return '';

  const withoutHash = value.split('#')[0];
  const querySource = withoutHash.includes('?')
    ? withoutHash.slice(withoutHash.indexOf('?') + 1)
    : withoutHash;

  return querySource
    .split('&')[0]
    .trim()
    .replace(/^\/+/, '')
    .replace(/^\?+/, '')
    .replace(/=.*$/, '');
};

export const getHomeDeeplinkPath = (page) => {
  const sections = Array.isArray(page?.sections) ? page.sections : [];
  const heroSeccionesBlock = sections.find(
    (section) => section?.__component === 'blocks.hero-secciones'
  );
  const heroSections = Array.isArray(heroSeccionesBlock?.secciones)
    ? heroSeccionesBlock.secciones
    : [];
  const lastSlug = normalizeHomeDeeplinkSlug(heroSections[heroSections.length - 1]?.slug);

  return lastSlug ? `/?${lastSlug}` : '';
};
